import React, { useCallback, useEffect, useState } from 'react';
import { fromDatetimeLocalVancouver } from '../../lib/utils';
import { useAdminApi, errorMessage } from './adminApi';
import { useQueryParams } from './useQueryParams';
import { formatDate, formatDateTime } from './format';
import {
  Badge, DataTable, Drawer, ListLayout, Pagination, Panel, SearchInput, Spinner, Toolbar,
  btnDanger, btnPrimary, btnSecondary, inputClass, labelClass
} from './AdminUI';

const EMPTY_BULLETIN = { title: '', description: '', audience: 'all', eventTitle: '', startTime: '', endTime: '' };
const toIso = (local) => fromDatetimeLocalVancouver(local).toISOString();

function AudienceBadge({ audience }) {
  return audience === 'owners' ? <Badge className="text-audienceOwners border-audienceOwners">Owners only</Badge> : <Badge>All residents</Badge>;
}

function BulletinForm({ condo, bulletin, onSaved, onCancel }) {
  const api = useAdminApi();
  const [form, setForm] = useState(() => (bulletin
    ? { ...EMPTY_BULLETIN, title: bulletin.title || '', description: bulletin.description || '', audience: bulletin.audience || 'all' }
    : EMPTY_BULLETIN));
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (bulletin) {
        await api.put(`/api/bulletins/${bulletin._id}`, { title: form.title, description: form.description, audience: form.audience });
        onSaved('Bulletin saved.');
      } else {
        const body = { condoId: condo._id, title: form.title, description: form.description, audience: form.audience };
        if (form.eventTitle && form.startTime && form.endTime) {
          body.calendarEvent = { title: form.eventTitle, startTime: toIso(form.startTime), endTime: toIso(form.endTime) };
        }
        await api.post('/api/bulletins', body);
        onSaved('Bulletin submitted for approval. Review it in the Approvals tab.');
      }
    } catch (err) {
      alert(errorMessage(err, 'Failed to save bulletin'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <label htmlFor="bulletin-title" className={labelClass}>Title *</label>
        <input id="bulletin-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputClass} placeholder="e.g., Fire Inspection Notice" />
      </div>
      <div>
        <label htmlFor="bulletin-description" className={labelClass}>Description *</label>
        <textarea id="bulletin-description" required rows={6} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputClass} placeholder="Provide details about this bulletin..." />
      </div>
      <div>
        <label htmlFor="bulletin-audience" className={labelClass}>Audience</label>
        <select id="bulletin-audience" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} className={inputClass}>
          <option value="all">All Residents</option>
          <option value="owners">Owners Only</option>
        </select>
      </div>
      {!bulletin && (
        <fieldset className="border-t border-border pt-3 space-y-2">
          <legend className="text-sm font-semibold text-foreground">Calendar event (optional)</legend>
          <input aria-label="Event title" value={form.eventTitle} onChange={(e) => setForm({ ...form, eventTitle: e.target.value })} className={inputClass} placeholder="e.g., Fire Inspection" />
          <div className="grid grid-cols-2 gap-2">
            <input aria-label="Event start" type="datetime-local" value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} className={inputClass} />
            <input aria-label="Event end" type="datetime-local" value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} className={inputClass} />
          </div>
        </fieldset>
      )}
      {!bulletin && <p className="text-xs text-muted-foreground">New bulletins go to Approvals before residents see them.</p>}
      <div className="flex gap-2">
        <button type="submit" disabled={saving} className={btnPrimary}>{saving ? 'Saving...' : bulletin ? 'Save Changes' : 'Create Bulletin'}</button>
        <button type="button" onClick={onCancel} className={btnSecondary}>Cancel</button>
      </div>
    </form>
  );
}

function LinkedEvents({ condo, bulletin, onChanged, notify }) {
  const api = useAdminApi();
  const [linking, setLinking] = useState(false);
  const [events, setEvents] = useState(null);
  const [eventQuery, setEventQuery] = useState('');
  const [existingId, setExistingId] = useState('');
  const [newEvent, setNewEvent] = useState({ title: '', startTime: '', endTime: '' });
  const [busy, setBusy] = useState(false);
  const linked = bulletin.calendarEvents || [];

  useEffect(() => {
    if (!linking) return undefined;
    const timer = setTimeout(() => {
      api.get(`/api/admin/condos/${condo._id}/calendar-events`, { limit: 100, q: eventQuery || undefined })
        .then((response) => setEvents({ list: response.data.events || [], total: response.data.total || 0 }))
        .catch(() => setEvents({ list: [], total: 0 }));
    }, eventQuery ? 300 : 0);
    return () => clearTimeout(timer);
  }, [api, condo._id, linking, eventQuery]);

  const update = async (body, message, fallback) => {
    setBusy(true);
    try {
      await api.put(`/api/bulletins/${bulletin._id}`, body);
      notify(message);
      setLinking(false);
      setExistingId('');
      setNewEvent({ title: '', startTime: '', endTime: '' });
      onChanged();
    } catch (err) {
      alert(errorMessage(err, fallback));
    } finally {
      setBusy(false);
    }
  };

  const unlink = (event) => {
    if (!window.confirm(`Unlink "${event.title}" from this bulletin? The event itself is kept.`)) return;
    update({ unlinkEventId: event._id }, 'Event unlinked.', 'Failed to unlink event');
  };

  const createAndLink = (e) => {
    e.preventDefault();
    if (!newEvent.title || !newEvent.startTime || !newEvent.endTime) {
      alert('Please provide an event title, start time, and end time.');
      return;
    }
    update({ newEvent: { title: newEvent.title, description: '', startTime: toIso(newEvent.startTime), endTime: toIso(newEvent.endTime) } }, 'Event created and linked.', 'Failed to create and link event');
  };

  return (
    <section className="mt-6">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-foreground">Calendar events ({linked.length})</h3>
        <button type="button" onClick={() => setLinking(!linking)} className="text-primary hover:text-primary/80 text-sm">{linking ? 'Cancel' : '+ Link Event'}</button>
      </div>
      {linked.length === 0 && !linking && <p className="text-sm text-muted-foreground">No linked events.</p>}
      <ul className="space-y-2">
        {linked.map((event) => (
          <li key={event._id} className="flex items-start gap-2 p-2 rounded border border-border">
            <div className="flex-1 text-sm">
              <p className="font-medium text-foreground">{event.title}</p>
              <p className="text-xs text-muted-foreground">{formatDateTime(event.startTime)} → {formatDateTime(event.endTime)}</p>
            </div>
            <button type="button" onClick={() => unlink(event)} disabled={busy} className="text-destructive hover:text-destructive/80 text-xs">Unlink</button>
          </li>
        ))}
      </ul>
      {linking && (
        <div className="mt-3 space-y-3 p-3 rounded border border-border">
          <div>
            <label htmlFor="link-existing-event" className={labelClass}>Link an existing event</label>
            <input
              type="search"
              value={eventQuery}
              onChange={(e) => setEventQuery(e.target.value)}
              placeholder="Search upcoming events..."
              aria-label="Search upcoming events"
              className={`${inputClass} mb-2`}
            />
            <div className="flex gap-2">
              <select id="link-existing-event" value={existingId} onChange={(e) => setExistingId(e.target.value)} className={inputClass}>
                <option value="">{events ? 'Select an event...' : 'Loading events...'}</option>
                {(events?.list || []).filter((ev) => !linked.some((l) => l._id === ev._id)).map((ev) => (
                  <option key={ev._id} value={ev._id}>{ev.title} ({formatDateTime(ev.startTime)})</option>
                ))}
              </select>
              <button type="button" onClick={() => existingId && update({ linkEventId: existingId }, 'Event linked.', 'Failed to link event')} disabled={busy || !existingId} className={btnPrimary}>Link</button>
            </div>
            {events && events.total > events.list.length && (
              <p className="text-xs text-muted-foreground mt-1">Showing the next {events.list.length} of {events.total} upcoming events. Search to find later ones.</p>
            )}
          </div>
          <form onSubmit={createAndLink} className="border-t border-border pt-3 space-y-2">
            <span className={labelClass}>Or create a new event</span>
            <input aria-label="New event title" value={newEvent.title} onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })} className={inputClass} placeholder="Event title" />
            <div className="grid grid-cols-2 gap-2">
              <input aria-label="New event start" type="datetime-local" value={newEvent.startTime} onChange={(e) => setNewEvent({ ...newEvent, startTime: e.target.value })} className={inputClass} />
              <input aria-label="New event end" type="datetime-local" value={newEvent.endTime} onChange={(e) => setNewEvent({ ...newEvent, endTime: e.target.value })} className={inputClass} />
            </div>
            <button type="submit" disabled={busy} className={btnPrimary}>Create &amp; Link</button>
          </form>
        </div>
      )}
    </section>
  );
}

function BulletinsTab({ condo, notify, onCountsChange }) {
  const api = useAdminApi();
  const [params, setParams] = useQueryParams();
  const [data, setData] = useState(null);

  const q = params.get('q') || '';
  const pageParam = Number(params.get('page')) || null;
  const selectedId = params.get('bulletin');
  const creating = params.get('new') === '1';

  const load = useCallback(async () => {
    try {
      const response = await api.get(`/api/admin/condos/${condo._id}/bulletins`, { q: q || undefined, page: pageParam || undefined, focus: pageParam ? undefined : selectedId || undefined });
      setData(response.data);
    } catch (err) {
      alert(errorMessage(err, 'Failed to fetch bulletins'));
    }
  }, [api, condo._id, q, pageParam, selectedId]);

  useEffect(() => { load(); }, [load]);

  const selected = data?.bulletins.find((b) => String(b._id) === String(selectedId));

  const remove = async (bulletin) => {
    if (!window.confirm(`Delete bulletin "${bulletin.title}"?`)) return;
    try {
      await api.del(`/api/bulletins/${bulletin._id}`);
      notify('Bulletin deleted.');
      setParams({ bulletin: null });
      load();
    } catch (err) {
      alert(errorMessage(err, 'Failed to delete bulletin'));
    }
  };

  const clearAll = async () => {
    if (!window.confirm(`Delete ALL bulletins and their calendar events in "${condo.name}"? This cannot be undone.`)) return;
    try {
      const response = await api.del(`/api/admin/condos/${condo._id}/bulletins`);
      notify(response.data.message);
      load();
    } catch (err) {
      alert(errorMessage(err, 'Failed to clear bulletins'));
    }
  };

  const columns = [
    { key: 'title', header: 'Title', className: 'font-medium text-foreground' },
    { key: 'audience', header: 'Audience', hideBelow: 'md', render: (b) => <AudienceBadge audience={b.audience} /> },
    { key: 'events', header: 'Events', hideBelow: 'md', render: (b) => (b.calendarEvents || []).length || '—' },
    { key: 'createdAt', header: 'Posted', className: 'whitespace-nowrap text-muted-foreground', render: (b) => formatDate(b.createdAt) }
  ];

  const drawer = creating ? (
    <Drawer title="New bulletin" onClose={() => setParams({ new: null })}>
      <BulletinForm condo={condo} onCancel={() => setParams({ new: null })} onSaved={(message) => { notify(message); setParams({ new: null }); onCountsChange(); }} />
    </Drawer>
  ) : selected ? (
    <Drawer title={selected.title} badges={<AudienceBadge audience={selected.audience} />} subtitle={`Posted ${formatDateTime(selected.createdAt)}`} onClose={() => setParams({ bulletin: null })}>
      <BulletinForm key={selected._id} condo={condo} bulletin={selected} onCancel={() => setParams({ bulletin: null })} onSaved={(message) => { notify(message); load(); }} />
      <LinkedEvents condo={condo} bulletin={selected} notify={notify} onChanged={load} />
      <div className="mt-6 pt-4 border-t border-border">
        <button type="button" onClick={() => remove(selected)} className={btnDanger}>Delete bulletin</button>
      </div>
    </Drawer>
  ) : null;

  return (
    <ListLayout drawer={drawer}>
      <Panel>
        <Toolbar>
          <h2 className="text-base font-semibold text-foreground mr-2">Bulletins{data ? ` (${data.total})` : ''}</h2>
          <SearchInput value={q} onChange={(value) => setParams({ q: value, page: null }, { replace: true })} placeholder="Search bulletins..." />
          <button type="button" onClick={() => setParams({ new: '1', bulletin: null })} className={btnPrimary}>+ Add Bulletin</button>
        </Toolbar>
        {!data ? <Spinner /> : (
          <>
            <DataTable
              label="Bulletins"
              columns={columns}
              rows={data.bulletins}
              selectedId={selectedId}
              onRowClick={(b) => setParams({ bulletin: b._id, new: null })}
              emptyMessage={q ? 'No bulletins match your search.' : 'No bulletins yet.'}
            />
            <Pagination page={data.page} pages={data.pages} total={data.total} limit={data.limit} onChange={(n) => setParams({ page: n === 1 ? null : n })} />
          </>
        )}
        {process.env.NODE_ENV === 'development' && data?.total > 0 && (
          <div className="pt-4 mt-4 border-t border-border">
            <button type="button" onClick={clearAll} className={btnDanger}>Clear all bulletins</button>
          </div>
        )}
      </Panel>
    </ListLayout>
  );
}

export default BulletinsTab;
